/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   21/10/2022
 */

import React from "react";
import { Card, CardContent, CardHeader, Stack, Typography, useTheme } from "@mui/material";
import { Employee, OpsGenieEvent } from "../interfaces/Global";
import { getEventType } from "./event-types";
import ViewportEvent, { eventSx } from "./ViewportEvent";
import { formatDistance } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "../dates-helper";
import ModelView, { AttributeConfig } from "../components/ModelView";
import Avatar from "../components/Avatar";

export interface OpsGenieViewportEventsProps {
	viewportRange: Interval
	rangeResolution: number
	employee: Employee
	event: OpsGenieEvent
}

export default function OpsGenieViewportEvent(props: OpsGenieViewportEventsProps) {

	const eventType = getEventType(props.event.typeId);
	const theme = useTheme();

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={props.employee.timeZone}
			event={{
				title: eventType?.name || "Calendar Event",
				typeId: eventType?.id || "OC",
				startDatetime: new Date(props.event.startDatetime),
				endDatetime: new Date(props.event.endDatetime)
			}}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			details={
				<EventDetails
					event={props.event}
					employee={props.employee}
				/>
			}
			viewportIcon={eventType?.icon}
			getViewportLabel={(width) => {
				if (!eventType) return null;
				let label = props.event.rotation.name;
				if (width <= 40) {
					return null;
				} else if (width <= 80) {
					label = eventType.id;
				} else if (width > 200) {
					label += " (" + formatDistance(props.event.endDatetime, props.event.startDatetime) + ")";
				}
				return label;
			}}
			sx={{
				...eventSx(theme, eventType?.parent?.id?.toLowerCase(), false),
				...eventSx(theme, props.event.typeId?.toLowerCase(), false),
				//zIndex: 30
			}}
		/>
	);

}

interface EventTooltipProps {
	event: OpsGenieEvent
	employee: Employee
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	const eventType = getEventType(event.typeId);
	const eStartDate = utcToZonedTime(event.startDatetime, employee.timeZone);
	const eEndDate = utcToZonedTime(event.endDatetime, employee.timeZone);

	return (
		<Stack>
			<div>{employee.displayName}</div>
			<Typography>OpsGenie Event</Typography>
			<Stack>OpsGenie Schedule Rotation Event</Stack>
			<Stack>{eventType?.name}</Stack>
			<Stack>Start Date: {format(eStartDate, 'Pp')}</Stack>
			<Stack>End Date: {format(eEndDate, 'Pp')}</Stack>
			<Stack>{formatDistance(eEndDate, eStartDate)}</Stack>
			<Stack>Schedule: {event.schedule.name}</Stack>
			<Stack>Rotation: {event.rotation.name}</Stack>
		</Stack>
	)

}

interface EventDetailsProps {
	event: OpsGenieEvent
	employee: Employee
}

function EventDetails({ event, employee }: EventDetailsProps) {

	const eventType = getEventType(event.typeId);

	const attributes: AttributeConfig<OpsGenieEvent>[] = [
		{ label: "Source", value: "OpsGenie" },
		{ label: "Type", value: eventType?.name },
		{ label: "Category", value: eventType?.parent?.name },
		{ attribute: "startDatetime", format: "datetime", label: "Start Date" },
		{ attribute: "endDatetime", format: "datetime", label: "End Date" },
		{ label: "Capacity Modifier", value: Math.round(event.capacityModifier*100) + "%" + (event.capacityModifier === 0 ? " (Unavailable)" : (event.capacityModifier === 1 ? " (Available)" : "")) },
		{ attribute: "schedule", value: event.schedule.name },
		{ attribute: "rotation", value: event.rotation.name },
	];

	return (
		<Card>
			<CardHeader
				avatar={<Avatar id={employee.id as string} />}
				title={"OpsGenie Event"}
				subheader={employee.displayName}
			/>
			<CardContent>
				<ModelView
					attributes={attributes}
					model={event}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 500,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
		</Card>
	);

}